// Admin-only "Deadlines"-Reiter: Überblick, welche Projekte im zeitlichen Rahmen
// sind und welche ihre Deadline überschritten haben — inkl. der vom Owner
// eingetragenen Verzugsgründe direkt in der Ansicht.
import { useMemo } from 'react';
import { useData } from './DataContext.jsx';
import { fmtDeadline, deadlineDaysLeft, capitalizeName } from './lib/format.js';
import { ProjectAvatar, Avatar } from './components.jsx';
import { MentionText } from './mention-input.jsx';
import { isOpenForDeadline, isOverdue, overdueDays } from './lib/deadlines.js';
import { IconCalendar } from './icons.jsx';

function Kpi({ label, value, tone }) {
  return (
    <div className="card card-pad" style={{ flex: 1, minWidth: 0 }}>
      <div className="eyebrow">{label}</div>
      <div className="serif" style={{ fontSize: 26, fontWeight: 500, marginTop: 8, color: tone || 'var(--ink)' }}>{value}</div>
    </div>
  );
}

// The reasons a project's deadline slipped — overrun statements + shift history,
// shown directly so the admin doesn't have to dig.
function ReasonLog({ project }) {
  const { overrunsByProject, deadlineChanges, profileById } = useData();
  const overruns = overrunsByProject[project.id] || [];
  const shifts = (deadlineChanges || []).filter(c => c.project_id === project.id);
  if (overruns.length === 0 && shifts.length === 0) return null;

  return (
    <div style={{ marginTop: 12, padding: 12, background: 'var(--bg-sunken)', borderRadius: 8, border: '1px solid var(--stroke)' }}>
      <div className="eyebrow" style={{ marginBottom: 8 }}>Verzugs-Stellungnahmen</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {overruns.map(o => {
          const author = o.acknowledged_by ? profileById(o.acknowledged_by) : null;
          return (
            <div key={o.id} style={{ borderLeft: '2px solid var(--crit)', paddingLeft: 10 }}>
              <div className="tiny" style={{ color: 'var(--ink-2)', fontWeight: 600 }}>
                Deadline {fmtDeadline(o.missed_deadline)} überschritten
                {' · '}
                {o.resolution === 'closed'
                  ? <span style={{ color: 'var(--ok)' }}>abgeschlossen</span>
                  : <span style={{ color: 'var(--accent)' }}>verschoben auf {fmtDeadline(o.new_deadline)}</span>}
              </div>
              <div className="tiny muted" style={{ marginTop: 2 }}>
                {new Date(o.acknowledged_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                {author ? ` · ${capitalizeName(author.full_name || author.email)}` : ''}
              </div>
              <div className="tiny" style={{ marginTop: 4, color: 'var(--ink-2)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                <MentionText text={o.reason}/>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProjectRow({ project, onNavigate }) {
  const { customerById, profileById, overrunForCurrentDeadline } = useData();
  const customer = project.customer_id ? customerById(project.customer_id) : null;
  const owner = project.created_by ? profileById(project.created_by) : null;
  const days = project.deadline ? deadlineDaysLeft(project.deadline) : null;
  const overdue = isOverdue(project);
  const addressed = overdue && !!overrunForCurrentDeadline(project);

  let badge;
  if (project.deadline == null) {
    badge = { text: 'keine Deadline', color: 'var(--ink-3)', bg: 'var(--bg-hover)' };
  } else if (overdue) {
    badge = { text: `${overdueDays(project.deadline)} ${overdueDays(project.deadline) === 1 ? 'Tag' : 'Tage'} überfällig`, color: 'var(--crit)', bg: 'var(--crit-soft)' };
  } else {
    badge = { text: days === 0 ? 'heute fällig' : `in ${days} ${days === 1 ? 'Tag' : 'Tagen'}`, color: days < 14 ? 'var(--warn)' : 'var(--ok)', bg: days < 14 ? 'var(--warn-soft)' : 'var(--ok-soft)' };
  }

  return (
    <div style={{ padding: '14px 16px', borderTop: '1px solid var(--stroke)' }}>
      <div
        onClick={() => onNavigate?.({ view: 'project', id: project.id })}
        style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
      >
        <ProjectAvatar project={project} size={34}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {project.title}
          </div>
          <div className="tiny muted" style={{ marginTop: 2 }}>
            {customer?.name || 'Ohne Kunde'} · Deadline {fmtDeadline(project.deadline)}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {owner && <Avatar user={owner} size={22}/>}
          <span className="tiny muted" style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {owner ? capitalizeName(owner.full_name || owner.email) : '—'}
          </span>
        </div>
        <div style={{
          padding: '4px 8px', borderRadius: 999, background: badge.bg, color: badge.color,
          fontSize: 11, fontWeight: 600, fontFamily: 'var(--mono)', whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          {addressed && <span title="Stellungnahme liegt vor">✓ </span>}{badge.text}
        </div>
      </div>
      <ReasonLog project={project}/>
    </div>
  );
}

function Group({ title, hint, projects, tone, onNavigate }) {
  return (
    <div className="card" style={{ marginBottom: 18, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: tone, flexShrink: 0 }}/>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{title}</span>
        <span className="tiny mono muted" style={{ marginLeft: 'auto' }}>{projects.length}</span>
      </div>
      {hint && <div className="tiny muted" style={{ padding: '0 16px 10px' }}>{hint}</div>}
      {projects.length === 0 ? (
        <div className="tiny muted" style={{ padding: '0 16px 16px' }}>Keine Projekte.</div>
      ) : (
        projects.map(p => <ProjectRow key={p.id} project={p} onNavigate={onNavigate}/>)
      )}
    </div>
  );
}

export function DeadlinesAdminPage({ onNavigate }) {
  const { projects, overrunForCurrentDeadline } = useData();

  const groups = useMemo(() => {
    const open = projects.filter(isOpenForDeadline);
    const overdueOpen = [];   // überschritten, noch keine Stellungnahme
    const overdueDone = [];   // überschritten, Stellungnahme liegt vor
    const onTrack = [];       // im Rahmen (Deadline in der Zukunft / heute)
    const noDeadline = [];    // offen, ohne Deadline

    for (const p of open) {
      if (p.deadline == null) { noDeadline.push(p); continue; }
      if (isOverdue(p)) {
        (overrunForCurrentDeadline(p) ? overdueDone : overdueOpen).push(p);
      } else {
        onTrack.push(p);
      }
    }
    const byDeadlineAsc = (a, b) => (a.deadline || '').localeCompare(b.deadline || '');
    overdueOpen.sort(byDeadlineAsc);
    overdueDone.sort(byDeadlineAsc);
    onTrack.sort(byDeadlineAsc);
    return { overdueOpen, overdueDone, onTrack, noDeadline };
  }, [projects, overrunForCurrentDeadline]);

  return (
    <div className="content">
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <IconCalendar size={12}/> Deadlines
        </div>
        <h1 className="serif" style={{ fontSize: 26, fontWeight: 500, margin: '6px 0 0' }}>Deadline-Überblick</h1>
        <div className="tiny muted" style={{ marginTop: 4 }}>
          Welche Projekte im zeitlichen Rahmen sind, welche überschritten wurden — mit den Verzugsgründen direkt in der Ansicht.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 22 }}>
        <Kpi label="Überschritten – offen" value={groups.overdueOpen.length} tone={groups.overdueOpen.length ? 'var(--crit)' : undefined}/>
        <Kpi label="Überschritten – geklärt" value={groups.overdueDone.length} tone="var(--ink-2)"/>
        <Kpi label="Im Rahmen" value={groups.onTrack.length} tone="var(--ok)"/>
      </div>

      <Group
        title="Überschritten – offen"
        hint="Deadline gerissen, der Owner hat noch keine Stellung genommen. Er wird beim Login dazu aufgefordert."
        projects={groups.overdueOpen} tone="var(--crit)" onNavigate={onNavigate}
      />
      <Group
        title="Überschritten – geklärt"
        hint="Deadline gerissen, Stellungnahme liegt vor."
        projects={groups.overdueDone} tone="var(--warn)" onNavigate={onNavigate}
      />
      <Group
        title="Im Rahmen"
        hint="Offene Projekte, deren Deadline noch nicht erreicht ist."
        projects={groups.onTrack} tone="var(--ok)" onNavigate={onNavigate}
      />
      {groups.noDeadline.length > 0 && (
        <Group
          title="Ohne Deadline"
          hint="Offene Projekte ohne gesetzte Deadline — werden nicht überwacht."
          projects={groups.noDeadline} tone="var(--ink-3)" onNavigate={onNavigate}
        />
      )}
    </div>
  );
}
